/**
 * Decoration Registry
 *
 * Central registry mapping decoration set names to their components.
 *
 * To add a new decoration set:
 * 1. Create a new directory under `components/decorations/sets/[set-name]/`
 * 2. Create `[SetName]Background.tsx` and optionally `[SetName]Nav.tsx`
 * 3. Add the set name to `VALID_DECORATION_SETS` in `lib/utils/decorations.ts`
 * 4. Import the components here and add them to the registry below
 */

import type { DecorationRegistry } from "@/components/decorations/types";
import { isDecorationSetName } from "@/lib/utils/decorations";
import { FallBackground } from "./sets/fall/FallBackground";
import { HalloweenBackground } from "./sets/halloween/HalloweenBackground";
import { HalloweenNav } from "./sets/halloween/HalloweenNav";
import { SummerBackground } from "./sets/summer/SummerBackground";
import { ValentinesBackground } from "./sets/valentines/ValentinesBackground";
import { WinterBackground } from "./sets/winter/WinterBackground";

/**
 * Decoration registry mapping set names to their components.
 *
 * Each entry must have:
 * - background: The background decoration component (required)
 * - nav: The navigation decoration component (optional)
 */
export const decorationRegistry: DecorationRegistry = {
  halloween: {
    background: HalloweenBackground,
    nav: HalloweenNav,
  },
  fall: {
    background: FallBackground,
  },
  winter: {
    background: WinterBackground,
  },
  valentines: {
    background: ValentinesBackground,
  },
  summer: {
    background: SummerBackground,
  },
  // Add future decoration sets here following the same pattern:
  // "partner-promo-x": {
  //   background: PartnerPromoXBackground,
  //   nav: PartnerPromoXNav, // optional
  // },
};

/**
 * Gets decoration set components by name.
 *
 * Accepts raw string input and performs normalization (trimming, lowercasing) and validation.
 * This allows callers to pass raw input (e.g., from environment variables, config files, API responses)
 * without needing type assertions, while still providing type-safe access to decoration components.
 *
 * @param name - The decoration set name as a raw string (will be normalized and validated)
 * @returns The decoration set components, or null if name is invalid/not found
 * @example
 * ```ts
 * // Accepts raw strings
 * getDecorationSet("halloween"); // returns halloween components
 * getDecorationSet(" halloween "); // normalizes whitespace, returns halloween components
 * getDecorationSet("invalid"); // returns null
 * ```
 */
export function getDecorationSet(
  name: string | null
): DecorationRegistry[keyof DecorationRegistry] | null {
  // Defensive null check: handle null/undefined inputs first
  if (!name) {
    return null;
  }

  // Normalize the name by trimming whitespace
  // This ensures names with leading/trailing whitespace (e.g., " halloween ") are
  // normalized to their canonical form ("halloween") before lookup
  const trimmedName = name.trim();

  // Whitespace validation: check if trimmed name is empty
  // This handles empty strings ("") and whitespace-only strings ("  ", "\t", "\n")
  if (!trimmedName) {
    return null;
  }

  // Normalize to lowercase for case-insensitive matching
  const normalizedName = trimmedName.toLowerCase();

  // Validate against valid decoration sets using type guard
  if (!isDecorationSetName(normalizedName)) {
    // Log warning in development mode for invalid decoration set names
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[Decorations] Invalid decoration set name: "${name}". Valid options are: ${Object.keys(decorationRegistry).join(", ")}.`
      );
    }
    return null;
  }

  // Type-safe lookup after validation
  const set = decorationRegistry[normalizedName];

  // Additional safety check (should never happen after isDecorationSetName validation)
  if (!set) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[Decorations] Decoration set "${normalizedName}" validated but not found in registry. This indicates a bug.`
      );
    }
    return null;
  }

  return set;
}
